import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Line,
    ComposedChart,
    Legend,
} from 'recharts';
import { messageAPI } from '../services/api.js';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { ALINEA_PROFILES } from '../utils/constants.js';
import MessagesGallery from './MessagesGallery.jsx';
import { resolveDeliveryId } from '../utils/messageDelivery.js';
import { surveyDateAtNoon } from '../utils/surveyDate.js';
import './Messages.css';
import './AdminMessages.css';

const FEEDBACK_COLORS = {
    useful: '#22c55e',
    regular: '#eab308',
    not_useful: '#ef4444',
};

const FEEDBACK_THANKS = {
    useful: '¡Gracias! Nos alegra que el mensaje te haya servido hoy.',
    regular: 'Gracias. Tomamos nota para afinar los mensajes.',
    not_useful: 'Gracias por tu honestidad. Lo tendremos en cuenta.',
};

const MESES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function mesEtiqueta(yyyyMm) {
    if (!yyyyMm?.includes('-')) return yyyyMm || '';
    const [y, m] = yyyyMm.split('-');
    const idx = parseInt(m, 10) - 1;
    return idx >= 0 && idx < 12 ? `${MESES_CORTO[idx]} ${y}` : yyyyMm;
}

function isFeedbackLeader(rol) {
    return rol === 'admin' || rol === 'superadmin' || rol === 'supervisor';
}

function profileLabel(type) {
    if (type === 'sin_perfil') return 'Sin perfil ALINEA';
    return ALINEA_PROFILES[type]?.name || type;
}

function WorkerMessagesView() {
    const [message, setMessage] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [feedbackSent, setFeedbackSent] = useState(false);
    const [feedbackThanks, setFeedbackThanks] = useState('');
    const [feedbackSending, setFeedbackSending] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');

    useEffect(() => {
        loadMessages();
    }, []);

    async function loadMessages() {
        try {
            const [currentRes, histRes] = await Promise.all([messageAPI.getHoy(), messageAPI.getHistory()]);
            if (currentRes.data.success) {
                const m = currentRes.data.data;
                setMessage({ ...m, deliveryId: resolveDeliveryId(m) ?? m?.deliveryId });
                if (m?.feedback) {
                    setFeedbackSent(true);
                    setFeedbackThanks(FEEDBACK_THANKS[m.feedback] || '✅ ¡Gracias por tu opinión!');
                }
            }
            if (histRes.data.success) setHistory(histRes.data.data || []);
        } catch (err) {
            setFeedbackError('No se pudo cargar los mensajes. Verifica que el servidor API esté en marcha.');
            console.error('Error cargando mensajes:', err);
        } finally {
            setLoading(false);
        }
    }

    async function handleFeedback(feedback) {
        const deliveryId = resolveDeliveryId(message);
        if (!deliveryId) {
            setFeedbackError('No se pudo vincular este mensaje. Recarga la página e inténtalo de nuevo.');
            return;
        }
        setFeedbackSending(true);
        setFeedbackError('');
        try {
            const res = await messageAPI.submitFeedback(deliveryId, feedback);
            setFeedbackSent(true);
            setFeedbackThanks(
                FEEDBACK_THANKS[feedback] || res.data?.message || '✅ ¡Gracias por tu opinión!'
            );
            setMessage((prev) => (prev ? { ...prev, feedback } : prev));
            const histRes = await messageAPI.getHistory();
            if (histRes.data.success) setHistory(histRes.data.data || []);
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.message ||
                'No se pudo guardar tu opinión. Intenta de nuevo.';
            setFeedbackError(msg);
            console.error('Error enviando feedback:', err);
        } finally {
            setFeedbackSending(false);
        }
    }

    if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

    return (
        <>
            <h2 className="mb-xs">💬 Retroalimentación</h2>
            <p className="text-muted mb-lg">Palabras que te acompañan — mensaje del día e historial</p>

            <div className="grid-2 messages-worker-grid">
                <div className="current-message-column">
                    <h3 className="mb-md">Tu mensaje del día</h3>
                    {message ? (
                        <div className="glass-card message-hero">
                            <p className="message-hero-text">"{message.content}"</p>
                            <span className="text-xs text-muted">{getCategoryLabel(message.type)}</span>
                            {!feedbackSent ? (
                                <div className="message-feedback">
                                    <p className="text-sm text-muted mb-md">¿Te sirvió este mensaje?</p>
                                    <div className="message-feedback-btns">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            disabled={feedbackSending}
                                            onClick={() => handleFeedback('useful')}
                                        >
                                            👏 Me sirvió
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            disabled={feedbackSending}
                                            onClick={() => handleFeedback('regular')}
                                        >
                                            😐 Regular
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            disabled={feedbackSending}
                                            onClick={() => handleFeedback('not_useful')}
                                        >
                                            👎 No me sirvió
                                        </button>
                                    </div>
                                    {feedbackError ? (
                                        <p className="text-sm text-muted mt-md" role="alert">
                                            {feedbackError}
                                        </p>
                                    ) : null}
                                </div>
                            ) : (
                                <p className="message-feedback-thanks mt-md" role="status">
                                    {feedbackThanks || '✅ ¡Gracias por tu opinión!'}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="glass-card text-center text-muted">No tienes mensajes nuevos por ahora.</div>
                    )}
                </div>

                <div className="history-column">
                    <h3 className="mb-md">Mensajes anteriores</h3>
                    {history.length > 0 ? (
                        <div className="message-history stagger">
                            {history.slice(0, 10).map((h, i) => (
                                <div key={i} className="glass-card message-history-item">
                                    <p className="text-sm">"{h.content}"</p>
                                    <div className="message-history-meta">
                                        <span className="text-xs text-muted">{getCategoryLabel(h.type)}</span>
                                        <span className="text-xs text-muted">
                                            {h.feedback === 'useful' ? '👍' : h.feedback === 'regular' ? '😐' : h.feedback === 'not_useful' ? '👎' : ''}{' '}
                                            {(surveyDateAtNoon(h.deliveredDate || h.delivered_at) || null)?.toLocaleDateString(
                                                'es-ES',
                                                { day: 'numeric', month: 'short', year: 'numeric' }
                                            ) ?? '—'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted text-sm">Aún no hay historial de mensajes.</p>
                    )}
                </div>
            </div>
        </>
    );
}

function canManageMessageBank(rol) {
    return rol === 'admin' || rol === 'superadmin';
}

function LeaderMessagesHub() {
    const { user } = useAuth();
    const { tenantId } = useTenantScope();
    const [searchParams, setSearchParams] = useSearchParams();
    const canManage = canManageMessageBank(user?.rol);
    const tabParam = searchParams.get('tab');
    const [leaderTab, setLeaderTab] = useState(
        tabParam === 'galeria' && canManage ? 'galeria' : 'estadisticas'
    );
    const [summary, setSummary] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        if (tabParam === 'galeria' && canManage) setLeaderTab('galeria');
    }, [tabParam, canManage]);

    function setTab(next) {
        setLeaderTab(next);
        if (next === 'galeria') setSearchParams({ tab: 'galeria' });
        else setSearchParams({});
    }

    useEffect(() => {
        if (leaderTab !== 'estadisticas') return undefined;
        let cancelled = false;
        setStatsLoading(true);
        (async () => {
            try {
                const data = await api.getMessagesCompanySummary();
                if (!cancelled) setSummary(data);
            } catch (err) {
                console.error(err);
                if (!cancelled) setSummary(null);
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [leaderTab, tenantId]);

    const feedbackChartData = useMemo(() => {
        return (summary?.company?.feedbackBreakdown || []).map((f) => ({
            nombre: f.label,
            cantidad: f.count,
            key: f.key,
        }));
    }, [summary]);

    const trendChartData = useMemo(() => {
        return (summary?.monthlyTrend || []).map((row) => ({
            mes: mesEtiqueta(row.month),
            entregas: row.deliveries,
            utiles: row.useful,
        }));
    }, [summary]);

    const typeChartData = useMemo(() => {
        return (summary?.byType || [])
            .filter((t) => t.deliveries > 0)
            .slice(0, 8)
            .map((t) => ({ nombre: t.label, entregas: t.deliveries, utiles: t.useful }));
    }, [summary]);

    const c = summary?.company;

    return (
        <>
            <header className="msg-leader-header">
                <div>
                    <h2 className="mb-xs">💬 Mensajes y retroalimentación</h2>
                    <p className="text-muted" style={{ margin: 0, maxWidth: '42rem', lineHeight: 1.5 }}>
                        Estadísticas de entrega y reacciones
                        {canManage ? ', y galería maestra de mensajes por perfil y categoría.' : ' de tu equipo.'}
                    </p>
                </div>
                <div className="tab-navigation">
                    <button
                        type="button"
                        className={`tab-btn ${leaderTab === 'estadisticas' ? 'active' : ''}`}
                        onClick={() => setTab('estadisticas')}
                    >
                        📊 Estadísticas
                    </button>
                    {canManage ? (
                        <button
                            type="button"
                            className={`tab-btn ${leaderTab === 'galeria' ? 'active' : ''}`}
                            onClick={() => setTab('galeria')}
                        >
                            🖼️ Galería maestra
                        </button>
                    ) : null}
                </div>
            </header>

            {leaderTab === 'galeria' && canManage ? (
                <div className="mt-lg">
                    <MessagesGallery autoEnsureBank />
                </div>
            ) : null}

            {leaderTab === 'estadisticas' ? (
                statsLoading ? (
                    <div className="flex items-center gap-sm text-muted py-lg">
                        <div className="spinner" style={{ width: 28, height: 28 }} />
                        Cargando estadísticas…
                    </div>
                ) : !summary ? (
                    <p className="text-muted">No se pudieron cargar las estadísticas.</p>
                ) : (
                    <>
            <div className="msg-resumen-kpis stagger">
                <div className="glass-card msg-resumen-kpi">
                    <span className="msg-resumen-kpi-label">Mensajes activos en banco</span>
                    <strong>{c.activeMessages}</strong>
                </div>
                <div className="glass-card msg-resumen-kpi">
                    <span className="msg-resumen-kpi-label">Entregas (30 días)</span>
                    <strong>{c.deliveries}</strong>
                </div>
                <div className="glass-card msg-resumen-kpi">
                    <span className="msg-resumen-kpi-label">Con respuesta</span>
                    <strong>
                        {c.withFeedback} <span className="msg-resumen-kpi-sub">({c.responseRate}%)</span>
                    </strong>
                </div>
                <div className="glass-card msg-resumen-kpi">
                    <span className="msg-resumen-kpi-label">«Me sirvió»</span>
                    <strong>
                        {c.useful} <span className="msg-resumen-kpi-sub">({c.usefulRate}% de respuestas)</span>
                    </strong>
                </div>
            </div>

            <div className="msg-resumen-charts grid-2 stagger">
                <div className="glass-card">
                    <h4 className="msg-chart-title">Reacciones recibidas</h4>
                    {feedbackChartData.length === 0 ? (
                        <p className="text-muted text-sm">Aún no hay reacciones registradas en el período.</p>
                    ) : (
                        <div className="msg-chart-box">
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={feedbackChartData} layout="vertical" margin={{ left: 8, right: 12 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis type="category" dataKey="nombre" width={95} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="cantidad" radius={[0, 6, 6, 0]} maxBarSize={32}>
                                        {feedbackChartData.map((row) => (
                                            <Cell key={row.key} fill={FEEDBACK_COLORS[row.key] || '#94a3b8'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="glass-card">
                    <h4 className="msg-chart-title">Tendencia mensual (6 meses)</h4>
                    {trendChartData.length === 0 ? (
                        <p className="text-muted text-sm">Sin entregas en el período.</p>
                    ) : (
                        <div className="msg-chart-box">
                            <ResponsiveContainer width="100%" height={220}>
                                <ComposedChart data={trendChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend formatter={(v) => (v === 'entregas' ? 'Entregas' : 'Me sirvió')} />
                                    <Bar dataKey="entregas" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                    <Line type="monotone" dataKey="utiles" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            {typeChartData.length > 0 && (
                <div className="glass-card mt-lg stagger">
                    <h4 className="msg-chart-title">Por categoría de mensaje</h4>
                    <div className="msg-chart-box msg-chart-box-tall">
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={typeChartData} margin={{ bottom: 48 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="nombre" tick={{ fontSize: 9 }} angle={-28} textAnchor="end" height={70} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="entregas" name="Entregas" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="utiles" name="Me sirvió" fill="#22c55e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            <div className="glass-card mt-lg stagger">
                <h4 className="msg-chart-title">Por área</h4>
                {summary.byArea.length === 0 ? (
                    <p className="text-muted text-sm">Sin entregas por área en el período.</p>
                ) : (
                    <div className="msg-area-grid">
                        {summary.byArea.map((area) => (
                            <div key={area.areaId} className="msg-area-card">
                                <div className="msg-area-card-head">
                                    <strong>{area.areaName}</strong>
                                    <span className="text-xs text-muted">{area.deliveries} entregas</span>
                                </div>
                                <div className="msg-area-bar" aria-hidden>
                                    {area.useful > 0 && (
                                        <span className="msg-area-seg" style={{ width: `${(area.useful / Math.max(area.withFeedback, 1)) * 100}%`, background: FEEDBACK_COLORS.useful }} />
                                    )}
                                    {area.regular > 0 && (
                                        <span className="msg-area-seg" style={{ width: `${(area.regular / Math.max(area.withFeedback, 1)) * 100}%`, background: FEEDBACK_COLORS.regular }} />
                                    )}
                                    {area.notUseful > 0 && (
                                        <span className="msg-area-seg" style={{ width: `${(area.notUseful / Math.max(area.withFeedback, 1)) * 100}%`, background: FEEDBACK_COLORS.not_useful }} />
                                    )}
                                </div>
                                <p className="text-xs text-muted" style={{ margin: '0.35rem 0 0', lineHeight: 1.4 }}>
                                    Respuestas: {area.withFeedback} ({area.responseRate}%) · Útiles: {area.usefulRate}%
                                    <br />
                                    👏 {area.useful} · 😐 {area.regular} · 👎 {area.notUseful}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="glass-card mt-lg stagger">
                <h4 className="msg-chart-title">Por perfil ALINEA del colaborador</h4>
                {summary.byProfile.length === 0 ? (
                    <p className="text-muted text-sm">Sin datos por perfil.</p>
                ) : (
                    <div className="msg-profile-grid">
                        {summary.byProfile.map((row) => (
                            <div key={row.profileType} className="msg-profile-card">
                                <span className="msg-profile-emoji">
                                    {row.profileType === 'sin_perfil' ? '❔' : ALINEA_PROFILES[row.profileType]?.emoji || '•'}
                                </span>
                                <div>
                                    <strong className="text-sm">{profileLabel(row.profileType)}</strong>
                                    <p className="text-xs text-muted" style={{ margin: '0.2rem 0 0' }}>
                                        {row.deliveries} entregas · {row.withFeedback} respuestas
                                        {row.withFeedback > 0 ? ` · ${row.usefulRate}% útiles` : ''}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
                    </>
                )
            ) : null}
        </>
    );
}

export default function Messages() {
    const { user } = useAuth();
    const isLeader = isFeedbackLeader(user?.rol);

    return (
        <div className="container container-wide animate-in messages-page">
            {isLeader ? <LeaderMessagesHub /> : <WorkerMessagesView />}
        </div>
    );
}

function getCategoryLabel(cat) {
    const labels = {
        general: '🌱 General',
        constante: '🟢 El Constante',
        impulsivo: '🔴 El Impulsivo',
        analitico: '🔵 El Analítico',
        social: '🟡 El Social',
        estado_mal: '💙 Para días difíciles',
        frase_pero: '✨ Transformación (Pero → Y)',
        frase_por_que: '🔄 Perspectiva (¿Por qué? → ¿Para qué?)',
        frase_y: '💎 Refuerzo (Y)',
        fase_escuchar: '👂 Escucha',
        fase_perdonar: '🤲 Procesa',
        fase_adaptar: '🌊 Ajusta',
        recordatorio_checkin: '⏰ Recordatorio',
        guia_operario: '🧭 Guía operario',
        guia_supervisor: '🤝 Guía supervisor',
        acompanamiento_alerta: '🟡 Acompañar alerta',
    };
    return labels[cat] || '🌱 Inspiración';
}
